import type * as React from 'react';

import { useTranslation } from '@crehana/i18n';
import ArrowIcon from '@crehana/icons/outline/ArrowRight';
import { CustomNextImage as Image } from '@crehana/next';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { Col, Container, type CuiTheme, Row } from '@crehana/ui';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import styled from 'styled-components';

import type { PreCampaignProps } from '@/views/Campaign/types';

import Microdegrees from './Microdegrees';

const Wrapper = styled.div`
  border-radius: 20px;
  background: linear-gradient(
    163.68deg,
    rgba(77, 77, 77, 0.32) 14.79%,
    rgba(145, 145, 145, 0) 141.37%,
    rgba(44, 35, 15, 0.08) 141.37%
  );
`;

const SeeMoreButton: React.FC<React.PropsWithChildren<{ href: string }>> = ({
  href,
}) => {
  const { t } = useTranslation();

  return (
    <div className="cursor-pointer flex items-center z-6">
      <ArrowIcon color="white" size={24} />
      <a href={href} className="font-button text-white ml-8">
        {' '}
        {t('SEE_MORE')}{' '}
      </a>
    </div>
  );
};

const Products: React.FC<
  React.PropsWithChildren<PreCampaignProps['preProducts']>
> = ({ backgroundDesktop, backgroundMobile }) => {
  const { t } = useTranslation();

  return (
    <div className="overflow-hidden relative">
      <div className="hidden lg:block absolute inset-0">
        {backgroundDesktop && (
          <Image
            objectFit="cover"
            objectPosition="top"
            alt="Background desktop"
            layout="fill"
            src={backgroundDesktop}
          />
        )}
      </div>
      <div className="lg:hidden absolute inset-0">
        {backgroundMobile && (
          <Image
            objectFit="cover"
            objectPosition="top"
            alt="Background mobile"
            layout="fill"
            src={backgroundMobile}
          />
        )}
      </div>
      <Container full={{ xs: true, sm: false }} className="pb-40 lg:pb-0">
        <Row>
          <Col
            xs={12}
            sm={10}
            lg={6}
            gutter={{ xs: 0, lg: 32 }}
            offset={{ xs: 0, sm: 1, lg: 0 }}
          >
            <div>
              <Wrapper className="flex flex-col mb-32 mt-40 sm:mt-12 lg:mt-0 overflow-hidden">
                <div className="px-32 py-20 sm:px-48 sm:py-40 text-center sm:text-left">
                  <h3 className="font-h2 text-white">
                    {t('PRODUCTS_PAYG_TITLE')}
                  </h3>
                  <p className="font-body3 text-white mt-24">
                    {t('PRODUCTS_PAYG_SUBTITLE')}
                  </p>
                </div>

                <div className="w-full h-212 relative">
                  {/* TODO: talk with the code owner to fix this code so we can enable this rule again */}
                  {/* eslint-disable-next-line jsx-a11y/alt-text */}
                  <Image
                    layout="fill"
                    src={
                      'https://crehana-vercel-images-prd.imgix.net/public/crehana-frontend-mf/automated-v2-ce15be78-ca5a-4cc3-baae-344332ea524e-payg.png?auto=format'
                    }
                    objectFit="cover"
                  />
                  <div className="absolute right-0 bottom-0 mr-32 mb-24">
                    <SeeMoreButton href="/cursos-online" />
                  </div>
                  <div className="absolute left-0 bottom-0 ml-32 mb-24">
                    <span className="font-label uppercase text-white">
                      Sofia Niño de Rivera
                    </span>
                  </div>
                </div>
              </Wrapper>
              <Wrapper className="flex flex-col mt-24 sm:mt-32 relative z-7">
                <div className="px-32 py-20 sm:px-48 sm:py-40 text-center sm:text-left">
                  <h3 className="font-h2 text-white">
                    {t('PRODUCTS_PACKS_TITLE')}
                  </h3>
                  <p className="font-body3 text-white mt-24">
                    {t('PRODUCTS_PACKS_SUBTITLE')}
                  </p>
                </div>
                <div className="w-full h-212 relative">
                  {/* TODO: talk with the code owner to fix this code so we can enable this rule again */}
                  {/* eslint-disable-next-line jsx-a11y/alt-text */}
                  <Image
                    layout="fill"
                    objectFit="cover"
                    src={
                      'https://crehana-vercel-images-prd.imgix.net/public/crehana-frontend-mf/automated-v2-3e6e8f8d-efc9-45c4-a09c-8fa95ac5f340-packs.png?auto=format'
                    }
                  />
                </div>
                <div className="ml-auto mr-32 mt-32 mb-24">
                  <SeeMoreButton href="/arma-tu-pack" />
                </div>
              </Wrapper>
            </div>
          </Col>

          <Col
            xs={12}
            sm={10}
            lg={6}
            gutter={{ xs: 0, lg: 32 }}
            offset={{ xs: 0, sm: 1, lg: 0 }}
            className="h-full lg:h-auto"
          >
            <Wrapper className="flex flex-col relative h-full w-full mt-24 sm:mt-40 lg:mt-0">
              <div className="px-32 py-20 sm:px-48 sm:py-40 text-center sm:text-left">
                <h3 className="font-h2 text-white">
                  {t('PRODUCTS_PREMIUM_TITLE')}
                </h3>
                <p className="font-body3 text-white mt-24">
                  {t('PRODUCTS_PREMIUM_SUBTITLE')}
                </p>
                <div className="ml-4 mt-32">
                  <SeeMoreButton href="/premiun" />
                </div>
              </div>
              <div
                className="relative lg:absolute bottom-0 w-full px-76"
                css={`
                  height: 400px;
                  ${({ theme }: { theme: CuiTheme }) =>
                    theme.breakpoints.up('md')} {
                    height: 654px;
                  }
                `}
              >
                {/* TODO: talk with the code owner to fix this code so we can enable this rule again */}
                {/* eslint-disable-next-line jsx-a11y/alt-text */}
                <Image
                  layout="fill"
                  objectFit="contain"
                  objectPosition="bottom"
                  src={
                    'https://crehana-vercel-images-prd.imgix.net/public/crehana-frontend-mf/automated-v2-1287a7f3-5738-40f7-8d0c-b159589ae6ee-premium.png?auto=format'
                  }
                />
              </div>
            </Wrapper>
          </Col>
        </Row>
        <Row>
          <Col
            xs={12}
            sm={10}
            lg={12}
            gutter={{ xs: 0, lg: 32 }}
            offset={{ xs: 0, sm: 1, lg: 0 }}
          >
            <Wrapper className="flex flex-col sm:pb-40 lg:pb-0 lg:pl-8 mt-40 ">
              <div className="px-32 py-20 sm:px-48 sm:py-40 text-center sm:w-4/5 m-auto">
                <h3 className="font-h2 text-white">
                  {t('PRODUCTS_MICRODEGREE_TITLE')}
                </h3>
                <p className="font-body3 text-white mt-24">
                  {t('PRODUCTS_MICRODEGREE_SUBTITLE')}
                </p>
              </div>
              <div className="w-full relative">
                <Microdegrees />
              </div>
            </Wrapper>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Products;
